import classNames from 'classnames';
import React, { useCallback } from 'react';
import './Splitter.less';
import { createDndEvent, useDragAndDrop } from 'hooks/useDragAndDrop';

export type SplitterOrientation = 'vertical' | 'horizontal';
export type SplitterVariant = 'solid' | 'transparent';

export interface ISplitterDelta {
  deltaX: number;
  deltaY: number;
}

export interface ISplitterProps extends Omit<React.HTMLProps<HTMLDivElement>, 'onDragStart' | 'onDragEnd'> {
  orientation: SplitterOrientation;
  variant?: SplitterVariant;
  onDragStart?: (position: ISplitterDelta) => void;
  onDragging?: (position: ISplitterDelta) => void;
  onDragEnd?: (position: ISplitterDelta) => void;
}

export function Splitter({
  orientation,
  variant,
  className,
  style,
  onDragEnd,
  onDragStart,
  onDragging,
  ...otherProps
}: ISplitterProps) {
  const { isActive, startPoint, endPoint, start } = useDragAndDrop({
    onDragStart: () => {
      onDragStart?.({
        deltaX: 0,
        deltaY: 0,
      });
    },
    onDragging: () => {
      onDragging?.({
        deltaX: endPoint.x - startPoint.x,
        deltaY: endPoint.y - startPoint.y,
      });
    },
    onDragEnd: () => {
      onDragEnd?.({
        deltaX: endPoint.x - startPoint.x,
        deltaY: endPoint.y - startPoint.y,
      });
    },
  });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      start(createDndEvent(e));
    },
    [start]
  );

  return (
    <div
      className={classNames('dpc-splitter', className, {
        'dpc-splitter--vertical': orientation === 'vertical',
        'dpc-splitter--horizontal': orientation === 'horizontal',
        'dpc-splitter--transparent': variant === 'transparent',
        'dpc-splitter--dragging': isActive,
      })}
      role="separator"
      onMouseDown={handleMouseDown}
      style={{
        ...style,
        ...{
          left: orientation === 'vertical' && isActive ? endPoint.x - startPoint.x : 0,
          top: orientation === 'horizontal' && isActive ? endPoint.y - startPoint.y : 0,
        },
      }}
      {...otherProps}
    ></div>
  );
}
